/**
 * Error codes for i18n error messages
 *
 * These codes are used across backend and frontend to provide
 * internationalized error messages to users.
 *
 * Format: errors.<domain>.<errorType>
 * Example: errors.department.duplicateAbbreviation
 */ export var ErrorCode = /*#__PURE__*/ function(ErrorCode) {
    // ============================================================
    // Department Errors
    // ============================================================
    ErrorCode["DEPARTMENT_ABBREVIATION_ALREADY_IN_USE"] = "errors.department.abbreviationAlreadyInUse";
    ErrorCode["DEPARTMENT_ABBREVIATION_DUPLICATE_IN_DATA"] = "errors.department.abbreviationDuplicateInData";
    ErrorCode["DEPARTMENT_DUPLICATE_ABBREVIATION"] = "errors.department.duplicateAbbreviation";
    ErrorCode["DEPARTMENT_NAME_ALREADY_IN_USE"] = "errors.department.nameAlreadyInUse";
    ErrorCode["DEPARTMENT_NAME_DUPLICATE_IN_DATA"] = "errors.department.nameDuplicateInData";
    ErrorCode["DEPARTMENT_NOT_FOUND"] = "errors.department.notFound";
    ErrorCode["DEPARTMENT_PARENT_NOT_FOUND"] = "errors.department.parentNotFound";
    ErrorCode["DEPARTMENT_SELF_PARENT"] = "errors.department.selfParent";
    ErrorCode["DEPARTMENT_CIRCULAR_DEPENDENCY"] = "errors.department.circularDependency";
    ErrorCode["DEPARTMENT_HAS_SUBDEPARTMENTS"] = "errors.department.hasSubdepartments";
    ErrorCode["DEPARTMENT_HAS_MEMBERS"] = "errors.department.hasMembers";
    ErrorCode["DEPARTMENT_NO_MANAGER"] = "errors.department.noManager";
    /**
   * Manual resolve rejected: the department already has a manager (a
   * concurrent auto-match or second tab won). Deliberate manager changes go
   * through the departments-step back-edit, never through resolve.
   */ ErrorCode["DEPARTMENT_ALREADY_RESOLVED"] = "errors.department.alreadyResolved";
    ErrorCode["INVALID_ID"] = "errors.common.invalidId";
    // ============================================================
    // OrgFunction Errors
    // ============================================================
    ErrorCode["ORG_FUNCTION_NOT_FOUND"] = "errors.orgFunction.notFound";
    ErrorCode["ORG_FUNCTION_MAIN_ROLE_INVALID_KIND"] = "errors.orgFunction.mainRoleInvalidKind";
    ErrorCode["ORG_FUNCTION_NAME_DUPLICATE_IN_DATA"] = "errors.orgFunction.nameDuplicateInData";
    /**
   * A membership row's orgFunctionName matches more than one candidate
   * (e.g. both a batch Funktion and a batch Team) — the row must be
   * disambiguated before import.
   */ ErrorCode["ORG_FUNCTION_NAME_AMBIGUOUS"] = "errors.orgFunction.nameAmbiguous";
    // Warning severity: the row is skipped, its memberships still link.
    ErrorCode["ORG_FUNCTION_ALREADY_EXISTS"] = "errors.orgFunction.alreadyExists";
    // ============================================================
    // User/Member Errors
    // ============================================================
    ErrorCode["USER_NOT_FOUND"] = "errors.user.notFound";
    /**
   * The write would leave no platform user who may sign in as
   * `platform_admin` — and only an admin can hand the role out again (ADR 0042).
   */ ErrorCode["PLATFORM_LAST_ADMIN"] = "errors.superAdmin.lastAdmin";
    /**
   * A bulk-import row references an email that matches no user in the
   * tenant. Discriminated by the RowIssue `field` (leadEmail | ownerEmail |
   * memberEmail) with metadata { email } — one code for all resolution slots.
   */ ErrorCode["USER_EMAIL_NOT_FOUND"] = "errors.user.emailNotFound";
    ErrorCode["USER_ALREADY_EXISTS"] = "errors.user.alreadyExists";
    ErrorCode["USER_DUPLICATE_EMAIL"] = "errors.user.duplicateEmail";
    /**
   * A bulk-import batch contains the same email on multiple rows — the
   * conflict is inside the uploaded data, not with an existing user
   * (mirrors DEPARTMENT_ABBREVIATION_DUPLICATE_IN_DATA).
   */ ErrorCode["USER_EMAIL_DUPLICATE_IN_DATA"] = "errors.user.emailDuplicateInData";
    ErrorCode["USER_INVALID_EMAIL"] = "errors.user.invalidEmail";
    ErrorCode["USER_INACTIVE"] = "errors.user.inactive";
    ErrorCode["USER_SUSPENDED"] = "errors.user.suspended";
    ErrorCode["USER_ALREADY_SUSPENDED"] = "errors.user.alreadySuspended";
    ErrorCode["USER_ALREADY_ACTIVE"] = "errors.user.alreadyActive";
    ErrorCode["USER_INVALID_STATUS"] = "errors.user.invalidStatus";
    // Invite / activation-link flows: the person's Zitadel account already set a
    // credential. Zitadel latches that state permanently, so an invite code can
    // never be minted again — the honest answer is "sign in directly", not a
    // retryable failure.
    ErrorCode["USER_ALREADY_HAS_CREDENTIALS"] = "errors.user.alreadyHasCredentials";
    // Zitadel SSO: the email is the person's sign-in identifier. Org-side
    // renames are gated to the identity's home org (staff identities excluded
    // entirely) — everyone else is pointed at support, i.e. the superadmin path.
    ErrorCode["USER_EMAIL_CHANGE_NOT_ALLOWED"] = "errors.user.emailChangeNotAllowed";
    // Zitadel SSO home-org move (superadmin-only): moving replaces the linked
    // Zitadel account, so eligibility is gated hard and a lost rebind race
    // surfaces as a conflict instead of silently clobbering the winner.
    ErrorCode["USER_HOME_ORG_MOVE_NO_IDENTITY"] = "errors.homeOrgMove.noIdentity";
    ErrorCode["USER_HOME_ORG_MOVE_IDENTITY_DISABLED"] = "errors.homeOrgMove.identityDisabled";
    ErrorCode["USER_HOME_ORG_MOVE_STAFF_REFUSED"] = "errors.homeOrgMove.staffRefused";
    ErrorCode["USER_HOME_ORG_MOVE_TARGET_NOT_SSO"] = "errors.homeOrgMove.targetNotSsoEnabled";
    ErrorCode["USER_HOME_ORG_MOVE_NO_MEMBERSHIP"] = "errors.homeOrgMove.noMembershipInTarget";
    ErrorCode["USER_HOME_ORG_MOVE_CONFLICT"] = "errors.homeOrgMove.conflict";
    ErrorCode["USER_HOME_ORG_MOVE_EMAIL_CONFLICT"] = "errors.homeOrgMove.emailConflict";
    ErrorCode["USER_HOME_ORG_MOVE_DEAD_EMAIL_SYNC"] = "errors.homeOrgMove.deadEmailSyncItem";
    // Sign-in reset: replaces the person's Zitadel account. Superadmins, and
    // system admins of the person's home org only; never for yourself, pending
    // invitees (resend instead), a disabled sign-in, a home org that is deleted
    // or federated, or while an email change has not reached the account.
    ErrorCode["SIGN_IN_RESET_NOT_PERMITTED"] = "errors.signInReset.notPermitted";
    ErrorCode["SIGN_IN_RESET_SELF"] = "errors.signInReset.self";
    ErrorCode["SIGN_IN_RESET_USER_NOT_ACTIVE"] = "errors.signInReset.userNotActive";
    ErrorCode["SIGN_IN_RESET_NO_HOME_ORG"] = "errors.signInReset.noHomeOrg";
    ErrorCode["SIGN_IN_RESET_HOME_ORG_DELETED"] = "errors.signInReset.homeOrgDeleted";
    ErrorCode["SIGN_IN_RESET_SSO_ENFORCED"] = "errors.signInReset.ssoEnforced";
    ErrorCode["SIGN_IN_RESET_CONFLICT"] = "errors.signInReset.conflict";
    ErrorCode["SIGN_IN_RESET_IDENTITY_DISABLED"] = "errors.signInReset.identityDisabled";
    ErrorCode["SIGN_IN_RESET_EMAIL_CHANGE_UNSETTLED"] = "errors.signInReset.emailChangeUnsettled";
    ErrorCode["EXTERNAL_MEMBER_NO_DEPARTMENT"] = "errors.user.externalMemberNoDepartment";
    ErrorCode["EXTERNAL_MEMBER_NO_TEAM"] = "errors.user.externalMemberNoTeam";
    ErrorCode["EXTERNAL_MEMBER_REQUIRES_ROLE"] = "errors.user.externalMemberRequiresRole";
    /**
   * The requested roles belong to the other app branch than the tenant they
   * were written into (customer roles into a partner tenant, or the reverse).
   * The CASL split is keyed on tenant type, so such a membership grants nothing
   * — it fails at the first request instead of at write time unless refused
   * here.
   */ ErrorCode["USER_TYPES_TENANT_TYPE_MISMATCH"] = "errors.user.userTypesTenantTypeMismatch";
    ErrorCode["MEMBER_IS_TEAM_LEAD"] = "errors.user.memberIsTeamLead";
    /**
   * The tenant's superadmin-set cap for that seat pool is already taken. One
   * code per pool so the message can name which limit was hit.
   */ ErrorCode["SEAT_LIMIT_SYSTEM_ADMINS_REACHED"] = "errors.seatLimit.systemAdminsReached";
    ErrorCode["SEAT_LIMIT_ORG_MANAGERS_REACHED"] = "errors.seatLimit.orgManagersReached";
    ErrorCode["SEAT_LIMIT_MEMBERS_REACHED"] = "errors.seatLimit.membersReached";
    /**
   * A capped seat pool holds more logins than it sells — only reachable by a
   * superadmin lowering a cap below usage. Every write is refused until the
   * tenant frees a seat.
   */ ErrorCode["SEAT_OVERRUN"] = "errors.seatOverrun";
    /**
   * More users were assigned to an app than its per-tenant seat cap allows. Org
   * managers reach every app implicitly and are never counted against it.
   */ ErrorCode["APP_SEAT_LIMIT_REACHED"] = "errors.seatLimit.appSeatsReached";
    /**
   * A named app seat was assigned to an org manager. They reach every app
   * implicitly, so the seat would grant nothing while consuming the cap.
   */ ErrorCode["APP_SEAT_ORG_MANAGER_NOT_SEATABLE"] = "errors.seatLimit.appSeatOrgManagerNotSeatable";
    /**
   * A per-tenant config was submitted for an app `getAppConfigSchema` knows no
   * schema for. Refused rather than stored, so the app never reads back a blob
   * nothing ever validated.
   */ ErrorCode["APP_CONFIG_NOT_CONFIGURABLE"] = "errors.appConfig.notConfigurable";
    /** The submitted config does not match the app's own config schema. */ ErrorCode["APP_CONFIG_INVALID"] = "errors.appConfig.invalid";
    // ============================================================
    // Role Lifecycle Errors
    // ============================================================
    /**
   * A member tried to strip their own self-managing role (delete, suspend, or
   * a userTypes/status/classification change that costs them the role). Refused
   * on every one of those paths, because the actor would lock themselves out of
   * the very surface needed to undo it.
   */ ErrorCode["ROLE_SELF_REMOVAL_FORBIDDEN"] = "errors.role.selfRemovalForbidden";
    /**
   * The change would leave the tenant with no working holder of a role that
   * administers its own users and has none above it (a partner_admin today).
   * Nothing inside the tenant could grant the role back, so the last holder is
   * held. Only holders who can actually exercise the role count — a suspended
   * or never-activated one does not keep the tenant manageable.
   */ ErrorCode["ROLE_LAST_HOLDER_FORBIDDEN"] = "errors.role.lastHolderForbidden";
    // ============================================================
    // Authentication Errors
    // ============================================================
    ErrorCode["AUTH_INVALID_PASSWORD"] = "errors.auth.invalidPassword";
    // New password equals current; guarded upfront because Zitadel 500s on it
    // (rationale in change-password.service.ts).
    ErrorCode["AUTH_PASSWORD_UNCHANGED"] = "errors.auth.passwordUnchanged";
    ErrorCode["AUTH_USER_NOT_FOUND"] = "errors.auth.userNotFound";
    ErrorCode["AUTH_UNAUTHORIZED"] = "errors.auth.unauthorized";
    // auth-scoped (not sso.*): switch-tenant and memberships endpoints will
    // return the same failure without any IdP involvement
    ErrorCode["AUTH_NO_MEMBERSHIP"] = "errors.auth.noMembership";
    // Tenant has sso.enforced — credentials live at the customer's federated
    // IdP, so the in-app password-change endpoint denies the request.
    ErrorCode["AUTH_SSO_REQUIRED"] = "errors.auth.ssoRequired";
    // Zitadel-mode tenant, but the user resolves to no Zitadel account —
    // self-service security operations are impossible until they are swept
    // or sign in via SSO once.
    ErrorCode["AUTH_NOT_ZITADEL_LINKED"] = "errors.auth.notZitadelLinked";
    // Sensitive credential-change endpoints answer this when the token's
    // authentication instant is older than the freshness window — the frontend
    // reacts by asking the user to re-authenticate.
    ErrorCode["AUTH_REAUTH_REQUIRED"] = "errors.auth.reauthRequired";
    // ============================================================
    // TOTP (2FA) Errors
    // ============================================================
    ErrorCode["TOTP_UNAVAILABLE"] = "errors.totp.unavailable";
    ErrorCode["TOTP_CODE_INVALID"] = "errors.totp.invalidCode";
    ErrorCode["TOTP_ALREADY_CONFIGURED"] = "errors.totp.alreadyConfigured";
    ErrorCode["TOTP_REGISTRATION_NOT_FOUND"] = "errors.totp.registrationNotFound";
    // ============================================================
    // Passkey Errors
    // ============================================================
    ErrorCode["PASSKEYS_UNAVAILABLE"] = "errors.passkeys.unavailable";
    // Removing this passkey would leave the account with no primary sign-in
    // method at all (password or IdP link) — an unrecoverable lockout, because
    // invite resend is latched shut and passkey re-registration needs the very
    // session being locked out.
    ErrorCode["PASSKEY_LAST_AUTH_METHOD"] = "errors.passkeys.lastAuthMethod";
    // ============================================================
    // SSO Errors
    // ============================================================
    ErrorCode["SSO_STATE_EXPIRED"] = "errors.sso.stateExpired";
    ErrorCode["SSO_EMAIL_MISMATCH"] = "errors.sso.emailMismatch";
    ErrorCode["SSO_EMAIL_UNVERIFIED"] = "errors.sso.emailUnverified";
    ErrorCode["SSO_EXCHANGE_FAILED"] = "errors.sso.exchangeFailed";
    // The callback arrived without the single-use transaction cookie that the
    // initiating browser was given at /start or /step-up, or the cookie's hash
    // did not match the stored state entry. Kept distinct from
    // SSO_STATE_EXPIRED so a browser-binding CSRF/session-fixation block is
    // never conflated with a benign TTL expiry in logs (RFC 9700).
    ErrorCode["SSO_TRANSACTION_INVALID"] = "errors.sso.transactionInvalid";
    // ============================================================
    // Recording Errors
    // ============================================================
    ErrorCode["RECORDING_INVALID_STATUS_FOR_ACTION"] = "errors.recording.invalidStatusForAction";
    ErrorCode["RECORDING_NAME_OR_DESCRIPTION_REQUIRED"] = "errors.recording.nameOrDescriptionRequired";
    ErrorCode["USER_NOT_RECORDING_STEP"] = "errors.recording.userNotRecordingStep";
    ErrorCode["NUDGE_TARGET_NOT_ACTIVE"] = "errors.recording.nudgeTargetNotActive";
    ErrorCode["DECISION_RULE_NOT_FOUND"] = "errors.recording.decisionRuleNotFound";
    ErrorCode["DECISION_HAS_NO_RULES"] = "errors.recording.decisionHasNoRules";
    // ============================================================
    // Process Errors
    // ============================================================
    ErrorCode["PROCESS_NOT_FOUND"] = "errors.process.notFound";
    ErrorCode["PROCESS_DUPLICATE_NAME"] = "errors.process.duplicateName";
    ErrorCode["PROCESS_CHAIN_TYPE_CONFLICT"] = "errors.processChain.typeConflict";
    ErrorCode["PROCESS_CHAIN_NOT_FOUND"] = "errors.processChain.notFound";
    ErrorCode["PROCESS_CHAIN_CIRCULAR"] = "errors.processChain.circularDependency";
    ErrorCode["PROCESS_CHAIN_SELF_PARENT"] = "errors.processChain.selfParent";
    ErrorCode["CONNECTION_NOT_FOUND"] = "errors.processConnection.notFound";
    ErrorCode["PROCESS_STEP_NOT_FOUND"] = "errors.processStep.notFound";
    ErrorCode["INVALID_OPERATION"] = "errors.process.invalidOperation";
    // ============================================================
    // Process Event Errors
    // ============================================================
    ErrorCode["PROCESS_EVENT_NOT_FOUND"] = "errors.processEvent.notFound";
    /**
   * Permanent invariant (not capability-gated): every process keeps at least
   * one start event, so the start node can never be demoted to intermediate.
   */ ErrorCode["PROCESS_EVENT_START_REQUIRED"] = "errors.processEvent.startRequired";
    // ============================================================
    // Process Draft Errors
    // ============================================================
    ErrorCode["DRAFT_NOT_FOUND"] = "errors.processDraft.notFound";
    ErrorCode["DRAFT_ALREADY_EXISTS"] = "errors.processDraft.alreadyExists";
    ErrorCode["DRAFT_PUBLISH_CONTENT_VALIDATION"] = "errors.processDraft.publishContentValidation";
    ErrorCode["DRAFT_PUBLISH_GRAPH_VALIDATION"] = "errors.processDraft.publishGraphValidation";
    ErrorCode["DRAFT_PUBLISH_CONFLICT"] = "errors.processDraft.publishConflict";
    ErrorCode["DRAFT_STEP_NOT_FOUND"] = "errors.processDraft.stepNotFound";
    /**
   * 409 from insert-between when the source→target successor edge no longer
   * exists (e.g. a double-fired request already consumed it). Clients treat
   * it as a soft no-op.
   */ ErrorCode["DRAFT_EDGE_NOT_FOUND"] = "errors.processDraft.edgeNotFound";
    ErrorCode["DRAFT_NOTE_NOT_FOUND"] = "errors.processDraft.noteNotFound";
    ErrorCode["DRAFT_HISTORY_NOT_FOUND"] = "errors.processDraft.historyNotFound";
    /**
   * Insert-between or branch refused: the graph shape the request assumes
   * does not hold (source is not terminal, target equals source, a start-event
   * source with anything but one target, a branch rule on a non-decision
   * source). `metadata.reason` discriminates for diagnostics.
   */ ErrorCode["DRAFT_INSERT_NOT_APPLICABLE"] = "errors.processDraft.insertNotApplicable";
    /**
   * The input an event is being attached to cannot take one — it does not
   * exist, is not an event input, or already carries an event.
   * `metadata.reason` discriminates for diagnostics.
   */ ErrorCode["DRAFT_EVENT_INPUT_NOT_LINKABLE"] = "errors.processDraft.eventInputNotLinkable";
    /** A step can only become an assignment once something feeds it. */ ErrorCode["DRAFT_CONVERT_REQUIRES_PREVIOUS_OUTPUT"] = "errors.processDraft.convertRequiresPreviousOutput";
    // ============================================================
    // Step Template Errors
    // ============================================================
    ErrorCode["STEP_TEMPLATE_NOT_FOUND"] = "errors.stepTemplate.notFound";
    /** A requested node id is not a step or event of this draft. */ ErrorCode["STEP_TEMPLATE_INVALID_SELECTION"] = "errors.stepTemplate.invalidSelection";
    /** A stored edge references a node outside the template. */ ErrorCode["STEP_TEMPLATE_CORRUPT"] = "errors.stepTemplate.corrupt";
    // ============================================================
    // System Errors
    // ============================================================
    ErrorCode["SYSTEM_NOT_FOUND"] = "errors.system.notFound";
    ErrorCode["SYSTEM_NAME_DUPLICATE_IN_DATA"] = "errors.system.nameDuplicateInData";
    // Warning severity: the row is skipped, existing system left untouched.
    ErrorCode["SYSTEM_ALREADY_EXISTS"] = "errors.system.alreadyExists";
    // ============================================================
    // App Errors
    // ============================================================
    /** No published registry entry for the requested appId. */ ErrorCode["APP_NOT_FOUND_IN_REGISTRY"] = "errors.app.notFoundInRegistry";
    /** The app was never unlocked for this tenant — a superadmin has to. */ ErrorCode["APP_NOT_AVAILABLE_FOR_TENANT"] = "errors.app.notAvailableForTenant";
    /** The app is not unlocked for this tenant, so it cannot be locked/managed. */ ErrorCode["APP_NOT_UNLOCKED"] = "errors.app.notUnlocked";
    /** The app is not installed for this tenant. */ ErrorCode["APP_NOT_INSTALLED"] = "errors.app.notInstalled";
    /** The app is installed but disabled, so its surfaces answer nothing. */ ErrorCode["APP_NOT_ACTIVE"] = "errors.app.notActive";
    /**
   * The app file does not exist — or is a worker-generated file belonging to
   * someone else, which is answered identically on purpose.
   */ ErrorCode["APP_FILE_NOT_FOUND"] = "errors.app.fileNotFound";
    ErrorCode["APP_FILE_TYPE_UNSUPPORTED"] = "errors.app.fileTypeUnsupported";
    ErrorCode["APP_FILE_TOO_LARGE"] = "errors.app.fileTooLarge";
    ErrorCode["APP_FILE_EXPIRED"] = "errors.app.fileExpired";
    // ============================================================
    // App Chat Errors
    // ============================================================
    ErrorCode["APP_CHAT_SESSION_NOT_FOUND"] = "errors.appChat.sessionNotFound";
    // ============================================================
    // Chat Errors (both the Bliksy and the app-chat arm)
    // ============================================================
    /**
   * The message a feedback verdict names is gone (regenerated between resolve
   * and write), or is not an assistant message at all.
   */ ErrorCode["CHAT_MESSAGE_NOT_FOUND"] = "errors.chat.messageNotFound";
    // ============================================================
    // Omniscient Errors
    // ============================================================
    ErrorCode["OMNISCIENT_EXPORT_NOT_FOUND"] = "errors.omniscient.exportNotFound";
    // ============================================================
    // Transcription Errors
    // ============================================================
    ErrorCode["AUDIO_FORMAT_UNSUPPORTED"] = "errors.transcription.audioFormatUnsupported";
    // ============================================================
    // Artifact Errors
    // ============================================================
    ErrorCode["ARTIFACT_NOT_FOUND"] = "errors.artifact.notFound";
    // ============================================================
    // External Contact Errors
    // ============================================================
    ErrorCode["EXTERNAL_CONTACT_NOT_FOUND"] = "errors.externalContact.notFound";
    // ============================================================
    // Organization Errors
    // ============================================================
    ErrorCode["ORGANIZATION_NOT_FOUND"] = "errors.organization.notFound";
    // ============================================================
    // Tenant Errors
    // ============================================================
    ErrorCode["TENANT_NOT_FOUND"] = "errors.tenant.notFound";
    ErrorCode["TENANT_EXPIRED"] = "errors.tenant.expired";
    ErrorCode["TENANT_CLONE_FAILED"] = "errors.tenant.cloneFailed";
    /**
   * A discard was refused by one of its guards and nothing was dropped. The
   * case an admin actually meets: the clone really did complete and the job is
   * stale bookkeeping, so the tenant is no longer the job's to destroy —
   * including a clone whose only failure was the demo Zitadel import, which
   * must be dismissed rather than discarded. The remaining guards (job still
   * running, its worker still stamping liveness, ownership nonce mismatch, a
   * target that is not a plain tenant ObjectId) refuse with this same code: the
   * admin-facing fact is identical, and the operator detail belongs in the logs.
   */ ErrorCode["TENANT_CLONE_DISCARD_REFUSED"] = "errors.tenant.cloneDiscardRefused";
    /**
   * An action was refused because a clone involving this tenant is still
   * running. Two callers, one fact:
   *
   * - deleting a tenant that a running clone is writing into (or copying from):
   *   deletion revokes the memberships and soft-deletes the row, and the
   *   detached clone would re-provision those memberships seconds later,
   *   leaving global registry state pointing at a deleted tenant;
   * - dismissing a running clone job: its worker is still writing heartbeats
   *   and transitions, and removing the document would blind the reaper while
   *   the clone keeps going.
   *
   * Neither is a way to cancel a clone — there is no cancel protocol. Wait for
   * the clone, or let it be parked, and then discard it.
   */ ErrorCode["TENANT_CLONE_IN_PROGRESS"] = "errors.tenant.cloneInProgress";
    /**
   * A tenant with the requested slug already exists. Slugs identify a tenant
   * in URLs and outbound deep-links, so create, clone, and update reject a
   * collision rather than mint an ambiguous duplicate. Soft-deleted tenants
   * free their slug for reuse (the check ignores them).
   */ ErrorCode["TENANT_SLUG_ALREADY_IN_USE"] = "errors.tenant.slugAlreadyInUse";
    /** The chosen parent would put the account inside itself. */ ErrorCode["ACCOUNT_PARENT_CYCLE"] = "errors.account.parentCycle";
    /** The group nests deeper than we are willing to walk. */ ErrorCode["ACCOUNT_PARENT_TOO_DEEP"] = "errors.account.parentTooDeep";
    /** The HubSpot company is already assigned to another account. */ ErrorCode["ACCOUNT_COMPANY_ALREADY_ASSIGNED"] = "errors.account.companyAlreadyAssigned";
    /**
   * The account runs a partner workspace, whose partner context hangs off
   * this company: moving or removing it needs whoever manages workspaces.
   */ ErrorCode["ACCOUNT_PARTNER_COMPANY_LOCKED"] = "errors.account.partnerCompanyLocked";
    /** No account with this id. */ ErrorCode["ACCOUNT_NOT_FOUND"] = "errors.account.notFound";
    /** No such company in the CRM. */ ErrorCode["HUBSPOT_COMPANY_NOT_FOUND"] = "errors.hubspot.companyNotFound";
    /**
   * A tenant-type change was refused because the tenant's own members would not
   * survive it. The CASL app split is keyed on tenant type, so retyping a
   * populated tenant across the partner/customer boundary would leave every
   * member holding roles of the branch the tenant just left — the whole tenant
   * locked out, with no write that caused it. Move the members first, or empty
   * the tenant.
   */ ErrorCode["TENANT_TYPE_CHANGE_STRANDS_MEMBERS"] = "errors.tenant.typeChangeStrandsMembers";
    /**
   * A HubSpot company id is already the forward link of a different tenant. A
   * company belongs to at most one tenant (its data-scope anchor), so the link
   * write rejects unless the admin confirms the overwrite (`forceHubspotLink`).
   */ ErrorCode["TENANT_HUBSPOT_COMPANY_ALREADY_LINKED"] = "errors.tenant.hubspotCompanyAlreadyLinked";
    // ============================================================
    // Partner Errors
    // ============================================================
    /**
   * The route serves the partner area but the caller's tenant is not
   * `tenantType: 'partner'`. Kept apart from a bare 403 so the answer names the
   * tenant type rather than reading as a revoked permission.
   */ ErrorCode["PARTNER_TENANT_REQUIRED"] = "errors.partner.tenantRequired";
    /** The action is reserved for partner_admin; the caller is partner_staff. */ ErrorCode["PARTNER_ADMIN_REQUIRED"] = "errors.partner.adminRequired";
    /**
   * A partner-tenant membership was written with no partner role. Unlike a
   * customer tenant, the partner area has no roleless surface — every page and
   * endpoint of it requires partner_admin or partner_staff.
   */ ErrorCode["PARTNER_MEMBER_REQUIRES_ROLE"] = "errors.partner.memberRequiresRole";
    /**
   * A partner-tenant membership was written as `external`. Partner members are
   * the partner firm's own staff; the partner CASL branch grants by role alone,
   * and EXTERNAL_MEMBER_REQUIRES_ROLE could only be satisfied with a customer
   * role, which a partner tenant cannot hold.
   */ ErrorCode["PARTNER_MEMBER_MUST_BE_INTERNAL"] = "errors.partner.memberMustBeInternal";
    /**
   * The partner tenant carries no `hubspotPartnerCompanyId`. Leads, employees
   * and sales activation all resolve through that company, so the HubSpot-backed
   * half of the partner area stays closed until a superadmin links it — an
   * operator task the partner cannot perform themselves.
   */ ErrorCode["PARTNER_HUBSPOT_NOT_LINKED"] = "errors.partner.hubspotNotLinked";
    /**
   * The submitting member has no HubSpot contact of their own — or one outside
   * this partner's company — so the lead has no Akquisiteur to be attributed
   * to. A partner_admin resolves it by activating that member for sales.
   */ ErrorCode["PARTNER_SALES_NOT_ACTIVATED"] = "errors.partner.salesNotActivated";
    /** The member already holds a HubSpot contact link — activation runs once. */ ErrorCode["PARTNER_SALES_ALREADY_ACTIVATED"] = "errors.partner.salesAlreadyActivated";
    /**
   * Sales activation is only allowed for members who have accepted their
   * invitation (status `active`). The email on an invited-but-unaccepted member
   * is an unverified admin-typed string — activating it would search the whole
   * shared CRM by that email and adopt/associate the matching contact, turning
   * activation into a contact-poaching primitive and an email-existence oracle.
   */ ErrorCode["PARTNER_MEMBER_NOT_ACTIVE"] = "errors.partner.memberNotActive";
    ErrorCode["PARTNER_MATERIAL_NOT_FOUND"] = "errors.partner.materialNotFound";
    /** The material has no file in the requested language nor in any fallback. */ ErrorCode["PARTNER_MATERIAL_FILE_NOT_FOUND"] = "errors.partner.materialFileNotFound";
    /**
   * A confirmed upload does not match what was handed out: a foreign storage
   * key, a missing object, an oversized file, a format partners may not
   * receive, or a video instead of a file material. Guards the confirm step against pointing a material at an
   * arbitrary object in the bucket.
   */ ErrorCode["PARTNER_MATERIAL_UPLOAD_INVALID"] = "errors.partner.materialUploadInvalid";
    ErrorCode["PARTNER_NEWS_NOT_FOUND"] = "errors.partner.newsNotFound";
    /**
   * A confirmed news picture does not match what was handed out: a foreign
   * storage key, a missing object, or one larger than allowed.
   */ ErrorCode["PARTNER_NEWS_IMAGE_INVALID"] = "errors.partner.newsImageInvalid";
    /**
   * The HubSpot contact is already the link target of a different Bliks user.
   * One contact anchors exactly one member's lead attribution, so the second
   * claim loses — whether detected by the probe or by the unique-index race.
   */ ErrorCode["PARTNER_CONTACT_ALREADY_LINKED"] = "errors.partner.contactAlreadyLinked";
    /**
   * The person on one side of the Bliks-user ↔ HubSpot-contact link has no
   * email: sales activation cannot match or mint a contact, and an employee
   * invitation has no address to go to.
   */ ErrorCode["PARTNER_MEMBER_NO_EMAIL"] = "errors.partner.memberNoEmail";
    /**
   * The HubSpot contact is associated with another partner's company, so this
   * tenant may not adopt it — the contact exists and legitimately belongs to
   * someone else.
   */ ErrorCode["PARTNER_CONTACT_NOT_ADOPTABLE"] = "errors.partner.contactNotAdoptable";
    // ============================================================
    // Initial Setup Errors
    // ============================================================
    ErrorCode["INITIAL_SETUP_ALREADY_COMPLETED"] = "errors.initialSetup.alreadyCompleted";
    /**
   * Steps 3-5 of the setup wizard (org-functions, systems, processes) and
   * completion require the structural anchor first: departments AND users
   * must have been initialized.
   */ ErrorCode["INITIAL_SETUP_PREREQUISITES_MISSING"] = "errors.initialSetup.prerequisitesMissing";
    /**
   * The org's Ersteinrichtung wizard has not been finished yet — mutations
   * outside the setup allowlist are blocked until initialization.completedAt
   * is set (ADR 0011).
   */ ErrorCode["INITIAL_SETUP_NOT_COMPLETED"] = "errors.initialSetup.notCompleted";
    /**
   * Wizard `updates[]` rejected: either the step's initialization stamp is
   * unset (seeded rows cannot exist yet), or the target row is not
   * wizard-editable (e.g. a user who was already invited/activated).
   * Not-found targets use DEPARTMENT_NOT_FOUND / USER_NOT_FOUND instead.
   */ ErrorCode["INITIAL_SETUP_UPDATE_NOT_ALLOWED"] = "errors.initialSetup.updateNotAllowed";
    /**
   * Wizard `removals[]` rejected: the entity is referenced by a required
   * field elsewhere (department ← processes; user ← systems/processes).
   * Metadata: `{ kind: 'department' | 'user', id, blockedBy: 'systems' | 'processes', count }`.
   * Nothing in the batch is written (ADR 0011, third amendment).
   */ ErrorCode["INITIAL_SETUP_REMOVAL_BLOCKED"] = "errors.initialSetup.removalBlocked";
    /**
   * `initialize-complete` integrity gate failed: departments without a resolved manager
   * and/or wizard-imported users without a department remain (gaps created by
   * surgical removals). Metadata:
   * `{ unresolvedDepartments: n, usersWithoutDepartment: n }`.
   */ ErrorCode["INITIAL_SETUP_INCOMPLETE"] = "errors.initialSetup.incomplete";
    // ============================================================
    // Data Sync Errors
    // ============================================================
    ErrorCode["DATA_SYNC_RUN_NOT_FOUND"] = "errors.dataSync.syncRunNotFound";
    /** The run lock: another sync run of this tenant is reading or applying. */ ErrorCode["DATA_SYNC_RUN_IN_PROGRESS"] = "errors.dataSync.syncRunInProgress";
    /** The failureCode of a run that was left reading or applying for 15 minutes. */ ErrorCode["DATA_SYNC_RUN_STALE"] = "errors.dataSync.syncRunStale";
    /**
   * Apply is allowed only from `review`. A retry is allowed only for a `done`
   * run with `failed` outcomes, while no later run exists and the source has
   * not been linked again since the run was applied.
   */ ErrorCode["DATA_SYNC_RUN_NOT_IN_REVIEW"] = "errors.dataSync.syncRunNotInReview";
    /**
   * The apply selection names an unknown difference, a blocked difference, or
   * a difference whose dependency is not selected. Metadata:
   * `{ differenceIds: string[] }`, the offending ids.
   */ ErrorCode["DATA_SYNC_SELECTION_INVALID"] = "errors.dataSync.selectionInvalid";
    /**
   * A Mitarbeiter row of an Excel upload carries an unknown, deleted,
   * duplicate or foreign id, or an id whose row email belongs to another
   * person; there is no fallback to email. Every Excel refusal carries
   * `DataSyncExcelRefusal` metadata: `{ sheet, indices: string[] }`, the
   * offending request rows of one sheet, none when the sheet as a whole is.
   */ ErrorCode["DATA_SYNC_EXCEL_INVALID_IDS"] = "errors.dataSync.excelInvalidIds";
    /** Two or more Mitarbeiter rows share one email, case-insensitively. */ ErrorCode["DATA_SYNC_EXCEL_DUPLICATE_EMAILS"] = "errors.dataSync.excelDuplicateEmails";
    /** Two or more rows of one structure sheet carry the same id. */ ErrorCode["DATA_SYNC_EXCEL_DUPLICATE_IDS"] = "errors.dataSync.excelDuplicateIds";
    /** A structure row's id is not a live entity of its sheet's type. */ ErrorCode["DATA_SYNC_EXCEL_UNKNOWN_IDS"] = "errors.dataSync.excelUnknownIds";
    /**
   * A structure row with an id carries the current name of another live
   * entity of its type that the sheet does not list: ids shifted or swapped.
   */ ErrorCode["DATA_SYNC_EXCEL_MISMATCHED_IDS"] = "errors.dataSync.excelMismatchedIds";
    /** Two or more rows without id of one structure sheet share a name. */ ErrorCode["DATA_SYNC_EXCEL_DUPLICATE_NAMES"] = "errors.dataSync.excelDuplicateNames";
    /**
   * A structure row without id names more than one live entity of its type
   * that the sheet does not list by id: it could pair with neither.
   */ ErrorCode["DATA_SYNC_EXCEL_NAME_MATCHES_SEVERAL"] = "errors.dataSync.excelNameMatchesSeveral";
    /** A department or Stelle cell names a structure sheet that has no rows: names that sheet. */ ErrorCode["DATA_SYNC_EXCEL_EMPTY_REFERENCED_SHEET"] = "errors.dataSync.excelEmptyReferencedSheet";
    /**
   * A department, parent or Stelle cell names more than one row, none of them
   * the referring row's current target: names the rows sharing the name.
   */ ErrorCode["DATA_SYNC_EXCEL_AMBIGUOUS_NAMES"] = "errors.dataSync.excelAmbiguousNames";
    /** A department, parent or Stelle cell names no row of the workbook: names the rows holding the cell. */ ErrorCode["DATA_SYNC_EXCEL_UNRESOLVED_REFERENCES"] = "errors.dataSync.excelUnresolvedReferences";
    /** Departments whose parent cells form a cycle. */ ErrorCode["DATA_SYNC_EXCEL_PARENT_CYCLE"] = "errors.dataSync.excelParentCycle";
    /** A lead or system owner email matches no Mitarbeiter row and no live user. */ ErrorCode["DATA_SYNC_EXCEL_UNKNOWN_EMAILS"] = "errors.dataSync.excelUnknownEmails";
    /**
   * A lead or system owner email names a person the upload removes: no
   * Mitarbeiter row lists them, and the data source manages them.
   */ ErrorCode["DATA_SYNC_EXCEL_UNLISTED_LEADS"] = "errors.dataSync.excelUnlistedLeads";
    /** The Mitarbeiter sheet has no rows (an empty upload would propose everyone as a leaver): names that sheet. */ ErrorCode["DATA_SYNC_EXCEL_NO_PEOPLE"] = "errors.dataSync.excelNoPeople";
    ErrorCode["DATA_SYNC_PERSONIO_NOT_LINKED"] = "errors.dataSync.personioNotLinked";
    ErrorCode["DATA_SYNC_PERSONIO_CREDENTIALS_REJECTED"] = "errors.dataSync.personioCredentialsRejected";
    /** The stored credentials do not open under the current key: link Personio again. */ ErrorCode["DATA_SYNC_PERSONIO_CREDENTIALS_UNREADABLE"] = "errors.dataSync.personioCredentialsUnreadable";
    /**
   * Absence is read only from a complete read, so a partial read produces no
   * differences: a sync run ends `failed` with this failureCode, the setup
   * read answers 409.
   */ ErrorCode["DATA_SYNC_PERSONIO_READ_FAILED"] = "errors.dataSync.personioReadFailed";
    // ============================================================
    // Assignment Errors
    // ============================================================
    ErrorCode["ASSIGNMENT_NOT_FOUND"] = "errors.assignment.notFound";
    ErrorCode["ASSIGNMENT_ALREADY_COMPLETED"] = "errors.assignment.alreadyCompleted";
    ErrorCode["ASSIGNMENT_ALREADY_ACCEPTED_BY_TEAMMATE"] = "errors.assignment.alreadyAcceptedByTeammate";
    ErrorCode["ASSIGNMENT_TASK_NOT_DEFINED"] = "errors.assignment.taskNotDefined";
    ErrorCode["ASSIGNMENT_RULES_NOT_DEFINED"] = "errors.assignment.rulesNotDefined";
    ErrorCode["ASSIGNMENT_OUTPUT_NOT_DEFINED"] = "errors.assignment.outputNotDefined";
    ErrorCode["ASSIGNMENT_ALREADY_FINISHED"] = "errors.assignment.alreadyFinished";
    ErrorCode["REASSIGNEE_NOT_FOUND"] = "errors.assignment.reassigneeNotFound";
    ErrorCode["ASSIGNMENT_NOT_EXTERNAL_CONTACT"] = "errors.assignment.notExternalContact";
    ErrorCode["RECORDING_DELEGATE_NOT_FOUND"] = "errors.assignment.recordingDelegateNotFound";
    // ============================================================
    // Task (Aufgabe) Errors
    // ============================================================
    ErrorCode["TASK_NOT_FOUND"] = "errors.task.notFound";
    ErrorCode["TASK_ALREADY_COMPLETED"] = "errors.task.alreadyCompleted";
    ErrorCode["TASK_ASSIGNEE_NOT_FOUND"] = "errors.task.assigneeNotFound";
    // ============================================================
    // Approval (Freigabe) Errors
    // ============================================================
    ErrorCode["APPROVAL_NOT_FOUND"] = "errors.approval.notFound";
    ErrorCode["APPROVAL_NOT_PENDING"] = "errors.approval.notPending";
    ErrorCode["APPROVAL_NOT_REVIEWER"] = "errors.approval.notReviewer";
    ErrorCode["APPROVAL_OWN_REQUEST"] = "errors.approval.ownRequest";
    ErrorCode["APPROVAL_ALREADY_DECIDED"] = "errors.approval.alreadyDecided";
    ErrorCode["APPROVAL_REASON_REQUIRED"] = "errors.approval.reasonRequired";
    ErrorCode["APPROVAL_OVERRIDE_NOT_ALLOWED"] = "errors.approval.overrideNotAllowed";
    ErrorCode["APPROVAL_NOT_REQUESTER"] = "errors.approval.notRequester";
    ErrorCode["APPROVAL_NOT_ADMIN"] = "errors.approval.notAdmin";
    // ============================================================
    // Merge Assignment Errors
    // ============================================================
    ErrorCode["MERGE_TARGET_NOT_FOUND"] = "errors.merge.targetNotFound";
    ErrorCode["MERGE_TARGET_NOT_COMPLETED"] = "errors.merge.targetNotCompleted";
    ErrorCode["MERGE_TARGET_WRONG_PROCESS"] = "errors.merge.targetWrongProcess";
    ErrorCode["MERGE_USER_NOT_RELATED"] = "errors.merge.userNotRelated";
    // Draft-editor card merge: survivor and merged refer to the same node.
    ErrorCode["MERGE_SAME_NODE"] = "errors.merge.sameNode";
    // ============================================================
    // Survey Errors
    // ============================================================
    ErrorCode["SURVEY_NOT_FOUND"] = "errors.survey.notFound";
    // The row asks this recipient nothing at all: a stranded row — the step's
    // author or its assigned User under variant-only settings (ADR 0023
    // decision 3) — has no question left, so a direct or stale-client submit
    // must not complete it.
    ErrorCode["SURVEY_NOT_APPLICABLE"] = "errors.survey.notApplicable";
    // Variant Check (ADR 0022): a verdict from a recipient the recording already
    // speaks for, or in a tenant that has the check switched off.
    ErrorCode["SURVEY_VARIANT_CHECK_NOT_APPLICABLE"] = "errors.survey.variantCheckNotApplicable";
    ErrorCode["SURVEY_VARIANT_DESCRIPTION_REQUIRED"] = "errors.survey.variantDescriptionRequired";
    // The row still owes this recipient a verdict, and the verdict cannot be
    // given later: completing without one would foreclose it for good.
    ErrorCode["SURVEY_VARIANT_VERDICT_REQUIRED"] = "errors.survey.variantVerdictRequired";
    // A verdict is immutable: only a re-survey asks again.
    ErrorCode["SURVEY_VARIANT_ALREADY_ANSWERED"] = "errors.survey.variantAlreadyAnswered";
    // The row is dismissed, which is terminal: a stale `?surveyId=` deep link
    // would otherwise reopen the dialog and overwrite the decline with answers.
    ErrorCode["SURVEY_ALREADY_DISMISSED"] = "errors.survey.alreadyDismissed";
    // ============================================================
    // Capacity Errors
    // ============================================================
    // A step assigned to a System or an ExternalContact carries no human effort,
    // so it is excluded from capacity before any source is read — a manual value
    // on it could never become effective.
    ErrorCode["CAPACITY_MANUAL_NOT_APPLICABLE"] = "errors.capacity.manualNotApplicable";
    // ============================================================
    // Entity Suggestion Errors
    // ============================================================
    ErrorCode["ENTITY_SUGGESTION_NOT_FOUND"] = "errors.entitySuggestion.notFound";
    ErrorCode["ENTITY_SUGGESTION_INVALID_TRANSITION"] = "errors.entitySuggestion.invalidTransition";
    ErrorCode["ENTITY_SUGGESTION_MISSING_ADMIN_FIELD"] = "errors.entitySuggestion.missingAdminField";
    // ============================================================
    // Generic HTTP Errors
    // ============================================================
    ErrorCode["VALIDATION_FAILED"] = "errors.validation.failed";
    ErrorCode["UNAUTHORIZED"] = "errors.http.401";
    ErrorCode["FORBIDDEN"] = "errors.http.403";
    ErrorCode["NOT_FOUND"] = "errors.http.404";
    ErrorCode["CONFLICT"] = "errors.http.409";
    ErrorCode["UNPROCESSABLE_ENTITY"] = "errors.http.422";
    ErrorCode["INTERNAL_ERROR"] = "errors.http.500";
    ErrorCode["SERVICE_UNAVAILABLE"] = "errors.http.503";
    // ============================================================
    // Validation Errors (Field-specific)
    // ============================================================
    ErrorCode["FIELD_REQUIRED"] = "errors.validation.required";
    ErrorCode["FILE_REQUIRED"] = "errors.validation.fileRequired";
    ErrorCode["FIELD_INVALID"] = "errors.validation.invalid";
    ErrorCode["FIELD_TOO_SHORT"] = "errors.validation.minLength";
    ErrorCode["FIELD_TOO_LONG"] = "errors.validation.maxLength";
    ErrorCode["FIELD_DUPLICATE"] = "errors.validation.duplicate";
    ErrorCode["FIELD_INVALID_EMAIL"] = "errors.validation.email";
    ErrorCode["FIELD_INVALID_URL"] = "errors.validation.url";
    ErrorCode["FIELD_INVALID_PASSWORD"] = "errors.validation.password";
    ErrorCode["FIELD_PASSWORDS_MISMATCH"] = "errors.validation.passwordsMismatch";
    return ErrorCode;
}({});

//# sourceMappingURL=error-codes.js.map